import React, { useState, useRef, useEffect, useMemo } from 'react';
import './RevenueChart.css';

const RevenueChart = ({ data, selectedMetric, filters, timeRange }) => {
  const activeMetric = selectedMetric || 'revenue';
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const canvasRef = useRef(null);

  const metrics = useMemo(() => ({
    revenue: { label: 'Revenue', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)', isCurrency: true },
    rpm: { label: 'RPM', color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)', isCurrency: true },
    cpm: { label: 'CPM', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)', isCurrency: true },
    pageviews: { label: 'Pageviews', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
    adImpressions: { label: 'Ad Impressions', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' },
    // Analytics metrics
    users: { label: 'Users', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
    sessions: { label: 'Sessions', color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' },
    engagementRate: { label: 'Engagement Rate', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)', isPercentage: true },
    avgEngagement: { label: 'Avg. Engagement', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)', isDuration: true },
    engagementTime: { label: 'Engagement Time', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)', isDuration: true },
    returningUserEngagementTime: { label: 'Returning User Engagement Time', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)', isDuration: true },
    bounceRate: { label: 'Bounce Rate', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)', isPercentage: true },
    newUsers: { label: 'New Users', color: '#06b6d4', bgColor: 'rgba(6, 182, 212, 0.1)' },
    returnUsers: { label: 'Return Users', color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.1)' },
    returningUsersPercentage: { label: '% Returning Visitors', color: '#a855f7', bgColor: 'rgba(168, 85, 247, 0.1)', isPercentage: true },
    engagedSessionsPerUser: { label: 'Engaged Sessions/User', color: '#a855f7', bgColor: 'rgba(168, 85, 247, 0.1)' },
    usersPerSession: { label: 'Users per Session', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)' },
    pageviewsPerUser: { label: 'Pageviews per User', color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' },
    desktopPercentage: { label: 'Desktop Users', color: '#84cc16', bgColor: 'rgba(132, 204, 22, 0.1)', isPercentage: true },
    mobilePercentage: { label: 'Mobile Users', color: '#84cc16', bgColor: 'rgba(132, 204, 22, 0.1)', isPercentage: true },
    tabletPercentage: { label: 'Tablet Users', color: '#84cc16', bgColor: 'rgba(132, 204, 22, 0.1)', isPercentage: true },
    rpu: { label: 'RPU', color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)', isCurrency: true }
  }), []);

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(entry => {
      const processed = {
        date: entry.date,
        revenue: entry.revenue || 0,
        rpm: entry.rpm || 0,
        cpm: entry.adImpressions > 0 ? (entry.revenue / entry.adImpressions) * 1000 : 0,
        pageviews: entry.pageviews || 0,
        adImpressions: entry.adImpressions || 0
      };
      
      // Add analytics metrics if present
      if (entry.users !== undefined) processed.users = entry.users || 0;
      if (entry.sessions !== undefined) processed.sessions = entry.sessions || 0;
      if (entry.engagementRate !== undefined) processed.engagementRate = entry.engagementRate || 0;
      if (entry.avgSessionDuration !== undefined) {
        processed.avgEngagement = entry.avgSessionDuration || 0;
        processed.engagementTime = entry.avgSessionDuration || 0;
      }
      if (entry.engagementTime !== undefined) processed.engagementTime = entry.engagementTime || 0;
      if (entry.returningUserEngagementTime !== undefined) processed.returningUserEngagementTime = entry.returningUserEngagementTime || 0;
      if (entry.bounceRate !== undefined) processed.bounceRate = entry.bounceRate || 0;
      if (entry.newUsers !== undefined) processed.newUsers = entry.newUsers || 0;
      if (entry.returnUsers !== undefined) processed.returnUsers = entry.returnUsers || 0;
      if (entry.returningUsers !== undefined) processed.returnUsers = entry.returningUsers || 0;
      if (entry.returningUsersPercentage !== undefined) processed.returningUsersPercentage = entry.returningUsersPercentage || 0;
      if (entry.engagedSessionsPerUser !== undefined) processed.engagedSessionsPerUser = entry.engagedSessionsPerUser || 0;
      if (entry.usersPerSession !== undefined) processed.usersPerSession = entry.usersPerSession || 0;
      if (entry.pageviewsPerUser !== undefined) processed.pageviewsPerUser = entry.pageviewsPerUser || 0;
      if (entry.desktopPercentage !== undefined) processed.desktopPercentage = entry.desktopPercentage || 0;
      if (entry.mobilePercentage !== undefined) processed.mobilePercentage = entry.mobilePercentage || 0;
      if (entry.tabletPercentage !== undefined) processed.tabletPercentage = entry.tabletPercentage || 0;
      if (entry.rpu !== undefined) processed.rpu = entry.rpu || 0;
      
      // Calculate returningUsersPercentage if not present
      if (processed.returningUsersPercentage === undefined && processed.users > 0) {
        const returningUsers = processed.returnUsers || 0;
        processed.returningUsersPercentage = (returningUsers / processed.users) * 100;
      }
      
      return processed;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [data]);

  const metricConfig = metrics[activeMetric] || metrics.revenue;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || processedData.length === 0) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const width = rect.width;
    const height = rect.height;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };

    ctx.clearRect(0, 0, width, height);

    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const values = processedData.map(d => {
      // Handle calculated metrics
      if (activeMetric === 'returningUsersPercentage') {
        const totalUsers = d.users || 0;
        const returningUsers = d.returnUsers || d.returningUsers || 0;
        return totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
      } else if (activeMetric === 'engagementTime') {
        return d.engagementTime || d.avgSessionDuration || d.avgEngagement || 0;
      } else if (activeMetric === 'returningUserEngagementTime') {
        // Prioritize returningUserEngagementTime - don't fallback to site-wide metrics
        return d.returningUserEngagementTime || 0;
      } else if (activeMetric === 'engagedSessionsPerUser') {
        return d.engagedSessionsPerUser || 0;
      }
      return d[activeMetric] || 0;
    });
    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);

    // Draw grid lines
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }

    // Draw Y-axis labels
    ctx.fillStyle = '#6b7280';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const value = maxValue - (maxValue / 5) * i;
      let label;
      if (metricConfig.isCurrency) {
        label = `$${value >= 1000 ? (value / 1000).toFixed(1) + 'K' : value.toFixed(0)}`;
      } else if (metricConfig.isPercentage) {
        label = `${value.toFixed(1)}%`;
      } else if (metricConfig.isDuration) {
        const minutes = Math.floor(value / 60);
        const seconds = Math.floor(value % 60);
        if (minutes > 0) {
          label = `${minutes}m ${seconds}s`;
        } else {
          label = `${seconds}s`;
        }
      } else if (activeMetric === 'engagedSessionsPerUser' || activeMetric === 'usersPerSession' || activeMetric === 'pageviewsPerUser') {
        label = value.toFixed(2);
      } else {
        label = value >= 1000 
          ? `${(value / 1000).toFixed(1)}K`
          : value.toFixed(0);
      }
      ctx.fillText(label, padding.left - 10, padding.top + (chartHeight / 5) * i + 4);
    }

    // Draw area
    ctx.fillStyle = metricConfig.bgColor;
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top + chartHeight);
    processedData.forEach((d, i) => {
      const x = padding.left + (chartWidth / (processedData.length - 1 || 1)) * i;
      let value = d[activeMetric] || 0;
      if (activeMetric === 'returningUsersPercentage') {
        const totalUsers = d.users || 0;
        const returningUsers = d.returnUsers || d.returningUsers || 0;
        value = totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
      } else if (activeMetric === 'engagementTime') {
        value = d.engagementTime || d.avgSessionDuration || d.avgEngagement || 0;
      } else if (activeMetric === 'returningUserEngagementTime') {
        // Prioritize returningUserEngagementTime - don't fallback to site-wide metrics
        value = d.returningUserEngagementTime || 0;
      } else if (activeMetric === 'engagedSessionsPerUser') {
        value = d.engagedSessionsPerUser || 0;
      }
      const y = padding.top + chartHeight - ((value - minValue) / (maxValue - minValue || 1)) * chartHeight;
      if (i === 0) {
        ctx.lineTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.lineTo(padding.left + chartWidth, padding.top + chartHeight);
    ctx.closePath();
    ctx.fill();

    // Draw line
    ctx.strokeStyle = metricConfig.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    processedData.forEach((d, i) => {
      const x = padding.left + (chartWidth / (processedData.length - 1 || 1)) * i;
      let value = d[activeMetric] || 0;
      if (activeMetric === 'returningUsersPercentage') {
        const totalUsers = d.users || 0;
        const returningUsers = d.returnUsers || d.returningUsers || 0;
        value = totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
      } else if (activeMetric === 'engagementTime') {
        value = d.engagementTime || d.avgSessionDuration || d.avgEngagement || 0;
      } else if (activeMetric === 'returningUserEngagementTime') {
        // Prioritize returningUserEngagementTime - don't fallback to site-wide metrics
        value = d.returningUserEngagementTime || 0;
      } else if (activeMetric === 'engagedSessionsPerUser') {
        value = d.engagedSessionsPerUser || 0;
      }
      const y = padding.top + chartHeight - ((value - minValue) / (maxValue - minValue || 1)) * chartHeight;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.stroke();

    // Draw points
    ctx.fillStyle = metricConfig.color;
    processedData.forEach((d, i) => {
      const x = padding.left + (chartWidth / (processedData.length - 1 || 1)) * i;
      let value = d[activeMetric] || 0;
      if (activeMetric === 'returningUsersPercentage') {
        const totalUsers = d.users || 0;
        const returningUsers = d.returnUsers || d.returningUsers || 0;
        value = totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
      } else if (activeMetric === 'engagementTime') {
        value = d.engagementTime || d.avgSessionDuration || d.avgEngagement || 0;
      } else if (activeMetric === 'returningUserEngagementTime') {
        // Prioritize returningUserEngagementTime - don't fallback to site-wide metrics
        value = d.returningUserEngagementTime || 0;
      } else if (activeMetric === 'engagedSessionsPerUser') {
        value = d.engagedSessionsPerUser || 0;
      }
      const y = padding.top + chartHeight - ((value - minValue) / (maxValue - minValue || 1)) * chartHeight;
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fill();
    });

    // Draw X-axis labels
    ctx.fillStyle = '#6b7280';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'center';
    const labelInterval = Math.max(1, Math.floor(processedData.length / 6));
    processedData.forEach((d, i) => {
      if (i % labelInterval === 0 || i === processedData.length - 1) {
        const x = padding.left + (chartWidth / (processedData.length - 1 || 1)) * i;
        const date = new Date(d.date);
        const label = `${date.getMonth() + 1}/${date.getDate()}`;
        ctx.fillText(label, x, height - padding.bottom + 20);
      }
    });

  }, [processedData, activeMetric, metricConfig]);

  // Calculate values once for efficiency
  const chartValues = useMemo(() => {
    return processedData.map(d => {
      if (activeMetric === 'returningUsersPercentage') {
        const totalUsers = d.users || 0;
        const returningUsers = d.returnUsers || d.returningUsers || 0;
        return totalUsers > 0 ? (returningUsers / totalUsers) * 100 : 0;
      } else if (activeMetric === 'engagementTime') {
        return d.engagementTime || d.avgSessionDuration || d.avgEngagement || 0;
      } else if (activeMetric === 'returningUserEngagementTime') {
        return d.returningUserEngagementTime || 0;
      } else if (activeMetric === 'engagedSessionsPerUser') {
        return d.engagedSessionsPerUser || 0;
      }
      return d[activeMetric] || 0;
    });
  }, [processedData, activeMetric]);

  const maxValue = useMemo(() => Math.max(...chartValues, 1), [chartValues]);
  const minValue = useMemo(() => Math.min(...chartValues, 0), [chartValues]);

  // Handle mouse move to show tooltip
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || processedData.length === 0) {
      setHoveredPoint(null);
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = rect.width - padding.left - padding.right;
    const chartHeight = rect.height - padding.top - padding.bottom;

    // Find the closest point
    let closestPoint = null;
    let minDistance = Infinity;
    const pointRadius = 12; // Hover radius around each point

    processedData.forEach((d, i) => {
      const pointX = padding.left + (chartWidth / (processedData.length - 1 || 1)) * i;
      const value = chartValues[i];
      const pointY = padding.top + chartHeight - ((value - minValue) / (maxValue - minValue || 1)) * chartHeight;
      
      const distance = Math.sqrt(Math.pow(x - pointX, 2) + Math.pow(y - pointY, 2));
      if (distance < pointRadius && distance < minDistance) {
        minDistance = distance;
        const date = new Date(d.date);
        closestPoint = {
          x: pointX, // Use point's x position
          y: pointY - 30, // Position above the point
          value: value,
          date: `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
        };
      }
    });

    setHoveredPoint(closestPoint);
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  const formatValue = (value) => {
    if (metricConfig.isCurrency) {
      return `$${value >= 1000 ? (value / 1000).toFixed(2) + 'K' : value.toFixed(2)}`;
    }
    if (metricConfig.isPercentage || activeMetric === 'returningUsersPercentage') {
      return `${value.toFixed(2)}%`;
    }
    if (metricConfig.isDuration || activeMetric === 'engagementTime' || activeMetric === 'returningUserEngagementTime') {
      const minutes = Math.floor(value / 60);
      const seconds = Math.floor(value % 60);
      if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    }
    if (activeMetric === 'engagedSessionsPerUser' || activeMetric === 'usersPerSession' || activeMetric === 'pageviewsPerUser') {
      return value.toFixed(2);
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    return value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toLocaleString();
  };

  return (
    <div className="revenue-chart">
      <div className="chart-header">
        <h3>{metricConfig.label} Trend</h3>
      </div>
      <div className="chart-container">
        <canvas 
          ref={canvasRef} 
          className="chart-canvas"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
        {hoveredPoint && (
          <div className="chart-tooltip" style={{ left: hoveredPoint.x, top: hoveredPoint.y }}>
            <div className="tooltip-date">{hoveredPoint.date}</div>
            <div className="tooltip-value">
              {metricConfig.label}: {formatValue(hoveredPoint.value)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueChart;

